/* Orders & Cart API helpers. */

import { api } from "./api";

export type Stage =
  | "open"
  | "pending_confirmation"
  | "confirmed"
  | "sent_to_picking"
  | "needs_correction"
  | "picked"
  | "documents_in_preparation"
  | "documents_prepared"
  | "transport_requested"
  | "transport_confirmed"
  | "transport_label_received"
  | "dispatched"
  | "in_transit"
  | "goods_received"
  | "documents_need_correction"
  | "documents_confirmed"
  | "documents_sent_to_accounting"
  | "cancelled"
  // Legacy stages, still on rows created before the 2026-08 pipeline rework.
  | "in_preparation"
  | "ready_for_dispatch"
  | "received";

/** Stage → i18n key. Resolve at render time with `$_(STAGE_LABEL_KEYS[s])`
 *  so the visible label respects the active locale. */
export const STAGE_LABEL_KEYS: Record<Stage, string> = {
  open: "orders.stage.open",
  pending_confirmation: "orders.stage.pendingConfirmation",
  confirmed: "orders.stage.confirmed",
  sent_to_picking: "orders.stage.sentToPicking",
  needs_correction: "orders.stage.needsCorrection",
  picked: "orders.stage.picked",
  documents_in_preparation: "orders.stage.documentsInPreparation",
  documents_prepared: "orders.stage.documentsPrepared",
  transport_requested: "orders.stage.transportRequested",
  transport_confirmed: "orders.stage.transportConfirmed",
  transport_label_received: "orders.stage.transportLabelReceived",
  dispatched: "orders.stage.dispatched",
  in_transit: "orders.stage.inTransit",
  goods_received: "orders.stage.goodsReceived",
  documents_need_correction: "orders.stage.documentsNeedCorrection",
  documents_confirmed: "orders.stage.documentsConfirmed",
  documents_sent_to_accounting: "orders.stage.documentsSentToAccounting",
  cancelled: "orders.stage.cancelled",
  in_preparation: "orders.stage.inPreparation",
  ready_for_dispatch: "orders.stage.readyForDispatch",
  received: "orders.stage.received",
};

/** @deprecated Use `STAGE_LABEL_KEYS` + `$_` instead. Kept for any
 *  legacy non-Svelte caller; identical to the key map. */
export const STAGE_LABELS = STAGE_LABEL_KEYS;

/* ── Phases (audit OE-A11) ────────────────────────────────────────────────
   The 21-stage machine is the seller's operational reality; occasional users
   need the six commercial phases it folds into. Every stage maps to exactly
   one phase; the detail view features phase + owner + next action and keeps
   the raw stage list as the expandable audit trail. */

export type Phase =
  | "draft"
  | "commercial_review"
  | "fulfilment"
  | "transport"
  | "receipt"
  | "accounting"
  | "cancelled";

export const STAGE_PHASES: Record<Stage, Phase> = {
  open: "draft",
  pending_confirmation: "commercial_review",
  confirmed: "commercial_review",
  needs_correction: "commercial_review",
  sent_to_picking: "fulfilment",
  picked: "fulfilment",
  documents_in_preparation: "fulfilment",
  documents_prepared: "fulfilment",
  in_preparation: "fulfilment",
  ready_for_dispatch: "fulfilment",
  transport_requested: "transport",
  transport_confirmed: "transport",
  transport_label_received: "transport",
  dispatched: "transport",
  in_transit: "transport",
  goods_received: "receipt",
  received: "receipt",
  documents_need_correction: "receipt",
  documents_confirmed: "accounting",
  documents_sent_to_accounting: "accounting",
  cancelled: "cancelled",
};

export const PHASE_LABEL_KEYS: Record<Phase, string> = {
  draft: "orders.phase.draft",
  commercial_review: "orders.phase.commercialReview",
  fulfilment: "orders.phase.fulfilment",
  transport: "orders.phase.transport",
  receipt: "orders.phase.receipt",
  accounting: "orders.phase.accounting",
  cancelled: "orders.phase.cancelled",
};

/** Ordered phases for a progress strip (cancelled excluded). */
export const PHASE_ORDER: Phase[] = [
  "draft",
  "commercial_review",
  "fulfilment",
  "transport",
  "receipt",
  "accounting",
];

/** Which side owns the next move at this stage — feeds the "who acts next"
 *  line. `null` when the order is terminal. */
export function nextActionOwner(stage: Stage): "buyer" | "seller" | null {
  if (stage === "cancelled" || stage === "documents_sent_to_accounting") return null;
  const next = STAGE_NEXT[stage];
  if (!next) return null;
  return BUYER_STAGES.includes(next) ? "buyer" : "seller";
}

/** The main next step per stage (mirrors backend STAGE_TRANSITIONS). */
export const STAGE_NEXT: Partial<Record<Stage, Stage>> = {
  open: "pending_confirmation",
  pending_confirmation: "confirmed",
  confirmed: "sent_to_picking",
  sent_to_picking: "picked",
  needs_correction: "sent_to_picking",
  picked: "documents_in_preparation",
  documents_in_preparation: "documents_prepared",
  documents_prepared: "transport_requested",
  transport_requested: "transport_confirmed",
  transport_confirmed: "transport_label_received",
  transport_label_received: "dispatched",
  dispatched: "in_transit",
  in_transit: "goods_received",
  goods_received: "documents_confirmed",
  documents_need_correction: "documents_confirmed",
  documents_confirmed: "documents_sent_to_accounting",
  // Legacy stages re-enter the chain.
  in_preparation: "picked",
  ready_for_dispatch: "documents_in_preparation",
  received: "sent_to_picking",
};

/** Correction branches: the optional second step available at a stage. */
export const STAGE_BRANCH: Partial<Record<Stage, Stage>> = {
  confirmed: "needs_correction",
  sent_to_picking: "needs_correction",
  goods_received: "documents_need_correction",
};

/** Stages the buyer (заказчик) sets; the rest of the pipeline is the
 *  executor's. Cancellation is buyer-side too, listed separately. */
export const BUYER_STAGES: Stage[] = [
  "pending_confirmation",
  "goods_received",
  "documents_need_correction",
  "documents_confirmed",
  "documents_sent_to_accounting",
];
/** «Нужна коррекция» may be raised by either side. */
export const EITHER_SIDE_STAGES: Stage[] = ["needs_correction"];

/** Steps whose owner depends on where the order came from, keyed "from>to".
 *
 *  There is one: the invoice correction the buyer asks for once the goods have
 *  arrived. Confirming documents is otherwise the buyer's own act, but a
 *  correction is a request — the invoice is the sender's declaration, and only
 *  they can agree to restate it. Mirrors STAGE_SIDE_BY_ORIGIN on the server,
 *  which is what enforces it. */
export const STAGE_SIDE_BY_ORIGIN: Record<string, "buyer" | "seller"> = {
  "documents_need_correction>documents_confirmed": "seller",
};

/** Which side may move this order from `from` to `to`, or null for the
 *  ordinary rules (BUYER_STAGES / EITHER_SIDE_STAGES). */
export const stageSideFrom = (from: Stage, to: Stage): "buyer" | "seller" | null =>
  STAGE_SIDE_BY_ORIGIN[`${from}>${to}`] ?? null;

export const CANCELLABLE: Stage[] = ["open", "pending_confirmation", "in_preparation"];

export type CartItem = {
  id: number;
  product_id: number | null;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price_eur: number;
  vat_rate: number;
  line_total_eur: number;
  /** Pieces in one box — 1 when the product is sold loose. Lets the composer
   *  order in whole boxes and say what a quantity comes to. */
  packaging_qty: number;
  /** Explicit transport box size; null when the supplier has not filled it. */
  pcs_per_box?: number | null;
  unit: string;
  /** Smallest orderable quantity for the card — editors clamp to this. */
  min_order_qty: number;
  /** Server readiness verdicts for this line (PRICE_MISSING, BELOW_MOQ,
   *  NOT_PACKAGE_MULTIPLE, OUT_OF_STOCK, NOT_ACCESSIBLE). Empty = orderable.
   *  `suggested`/`min`/`multiple`/`available` ride along per code. */
  issues: CartLineIssue[];
  /** Bought off a discounted lot (2026-09-28): the lot, its best-before, the
   *  discount and what is left of it for this client. */
  lot_id?: number | null;
  batch_no?: string;
  expires_on?: string | null;
  expiry_discount_pct?: number | string | null;
  lot_available?: number | null;
  offer_kind?: string;
};

export type CartLineIssue = {
  code: string;
  product_id: number | null;
  message: string;
  min?: number;
  multiple?: number;
  suggested?: number;
  available?: number;
};

/** The smallest orderable quantity for a card — MOQ rounded up to a whole
 *  package. Mirrors apps/orders/readiness.first_valid_quantity; the server
 *  re-validates, this only picks sensible defaults in the UI. */
export function firstValidQuantity(p: { min_order_qty?: number | null; packaging_qty?: number | null }): number {
  const moq = Math.max(1, p.min_order_qty || 1);
  const mult = Math.max(1, p.packaging_qty || 1);
  return mult > 1 && moq % mult ? moq + (mult - (moq % mult)) : moq;
}

/** A legacy default of 1 does not describe a box; an explicit size of 1 does. */
export function hasBoxSize(p: { pcs_per_box?: number | null; packaging_qty?: number | null }): boolean {
  return (p.pcs_per_box ?? 0) > 0 || (p.packaging_qty ?? 1) > 1;
}

/** Shipment footer of the order summary — the numbers a transport-company
 *  request is made of, computed server-side from the product cards. */
export type OrderLogistics = {
  boxes_total: number;
  pallets_count: number;
  /** "800×1200" (mm), or "800×1200 / 1000×1200" when pallet types mix. */
  pallet_size: string;
  /** Computed height of each pallet, metres, tallest first. Decimals arrive
   *  from the API as strings ("0.54") — coerce before math. */
  pallet_heights_m: (number | string)[];
  goods_gross_kg: number | string | null;
  pallets_tare_kg: number | string;
  total_gross_kg: number | string | null;
  /** Lines whose product card lacks logistics data — excluded from the numbers. */
  incomplete: string[];
  /** The cards behind `incomplete`, index for index; null where deleted. */
  incomplete_ids?: (number | null)[];
  /** Distinct «характер груза» lines from the cards, for the carrier request. */
  cargo_natures: string[];
  needs_temp_control: boolean;
  /** Distinct storage regimes (e.g. "0°C+25°C") of the temp-controlled lines. */
  temp_regimes: string[];
};

export type Cart = {
  id: number;
  item_count: number;
  subtotal_eur: number;
  items: CartItem[];
  logistics: OrderLogistics | null;
  /** False while any line carries an issue (or the cart is empty) — the same
   *  verdict checkout enforces server-side. */
  sendable: boolean;
};

export type OrderItem = {
  id: number;
  product_id: number | null;
  product_name: string;
  product_sku: string;
  /** The barcode off the catalogue card, falling back to the internal code —
   *  the column the invoice prints, resolved server-side exactly as the PDF
   *  templates resolve it. */
  product_ean: string;
  /** g / kg / ml / l / pcs, off the card. */
  product_unit: string;
  quantity: number;
  unit_price_eur: number;
  vat_rate: number;
  line_total_eur: number;
  /** Bought off a discounted lot (2026-09-28); batch and best-before also
   *  ride a line the seller wrote with a lot. */
  lot_id?: number | null;
  batch_no?: string;
  expires_on?: string | null;
  expiry_discount_pct?: number | string | null;
  offer_kind?: string;
};

export type StageEvent = {
  from_stage: string;
  to_stage: string;
  note: string;
  created_at: string;
};

/** A correction of the lines after the order was sent — the seller's, made
 *  without the buyer agreeing, and read by both in «История» (2026-09-24). */
export type LineChange = {
  action: "added" | "changed" | "removed";
  product_name: string;
  quantity_before: number | null;
  quantity_after: number | null;
  actor_name: string;
  created_at: string;
};

export type Direction = "incoming" | "outgoing" | "internal" | "unrelated";

export type OrderListItem = {
  /** Taken off this company's list (shown under «Удалённые»). */
  deleted?: boolean;
  code: string;
  reference: string;
  local_seq: number | null;
  display_label: string;
  datetime_label: string;
  stage: Stage;
  direction: Direction;
  seller_slug: string;
  seller_name: string;
  buyer_slug: string;
  buyer_name_tenant: string;
  buyer_name: string;
  buyer_email: string;
  total_eur: number;
  item_count: number;
  downloaded_at: string | null;
  created_at: string;
};

export type OrderDetail = {
  allowed_transitions?: string[] | null;
  code: string;
  reference: string;
  local_seq: number | null;
  display_label: string;
  datetime_label: string;
  stage: Stage;
  direction: Direction;
  seller_slug: string;
  seller_name: string;
  seller_legal_name: string;
  seller_vat_id: string;
  seller_country: string;
  /** Requisites from the company's document profile (letterhead). */
  seller_reg_no: string;
  seller_legal_address: string;
  /** Where the goods actually sit, and the switchboard number — what a
   *  transport request needs rather than the registered office. */
  seller_physical_address: string;
  seller_phone: string;
  buyer_slug: string;
  buyer_name_tenant: string;
  buyer_legal_name: string;
  buyer_vat_id: string;
  buyer_country: string;
  buyer_reg_no: string;
  buyer_legal_address: string;
  buyer_physical_address: string;
  buyer_phone: string;
  buyer_name: string;
  buyer_email: string;
  shipping_address: string;
  notes: string;
  /** ISO datetime — when the buyer asked to receive the goods. */
  desired_delivery_at: string | null;
  /** «Экспорт» — the tick at the top of the folder that reveals the
   *  consignment note, the packing list and the transport booking. */
  is_export: boolean;
  /** «Заявка на транспорт» — the tick that opens the Logistic tab. */
  needs_transport?: boolean;
  /** «ПВН = 0» — every line counts at 0 % (the seller's call, 2026-09-23). */
  vat_zero?: boolean;
  subtotal_eur: number;
  vat_eur: number;
  total_eur: number;
  items: OrderItem[];
  stage_events: StageEvent[];
  line_changes?: LineChange[];
  /** Carries an invoice «Без заказа» — no lines of its own. */
  direct_invoice?: boolean;
  logistics: OrderLogistics | null;
  downloaded_at: string | null;
  created_at: string;
};

export type PaginatedOrders = { count: number; results: OrderListItem[] };

// Cart — `supplier` param scopes to a cross-tenant cart against that seller.
const _qs = (supplier?: string) => (supplier ? `?supplier=${encodeURIComponent(supplier)}` : "");

export const fetchCart = (slug: string, supplier?: string): Promise<Cart> =>
  api.get<Cart>(`/t/${slug}/cart${_qs(supplier)}`);

export const addToCart = (
  slug: string,
  product_id: number,
  quantity = 1,
  supplier?: string,
  /** A discounted lot of the card, bought as its own line (2026-09-28). */
  lot_id?: number,
): Promise<Cart> =>
  api.post<Cart>(`/t/${slug}/cart/items${_qs(supplier)}`, lot_id ? { product_id, quantity, lot_id } : { product_id, quantity });

export const updateCartItem = (
  slug: string,
  item_id: number,
  quantity: number,
  supplier?: string,
): Promise<Cart> => api.patch<Cart>(`/t/${slug}/cart/items/${item_id}${_qs(supplier)}`, { quantity });

/** Refresh the supplier's box sizes before rounding an already open order.
 *  Product cards may have changed since the composer loaded its cart. */
export async function roundCartToBoxes(slug: string, supplier: string): Promise<Cart> {
  let cart = await fetchCart(slug, supplier);
  const items = cart.items;
  for (const item of items) {
    // Discounted lots are bought as the remaining pieces, as in setQty.
    if (item.lot_id && item.expiry_discount_pct != null) continue;
    const pack = Math.max(1, item.packaging_qty ?? 1);
    const quantity = Math.ceil(item.quantity / pack) * pack;
    if (quantity !== item.quantity) {
      cart = await updateCartItem(slug, item.id, quantity, supplier);
    }
  }
  return cart;
}

export const removeCartItem = (
  slug: string,
  item_id: number,
  supplier?: string,
): Promise<void> => api.delete(`/t/${slug}/cart/items/${item_id}${_qs(supplier)}`);

export const clearCart = (slug: string, supplier?: string): Promise<void> =>
  api.delete(`/t/${slug}/cart${_qs(supplier)}`);

export const checkout = (
  slug: string,
  data: {
    buyer_name?: string;
    buyer_email?: string;
    shipping_address?: string;
    notes?: string;
    desired_delivery_at?: string;
    /** Default true: create AND send in one server transaction. False saves
     *  an `open` draft the supplier cannot see. */
    submit?: boolean;
    /** Retry-safe checkout: the same key never creates a second order. */
    idempotency_key?: string;
  },
  supplier?: string,
): Promise<OrderDetail> =>
  api.post<OrderDetail>(`/t/${slug}/cart/checkout${_qs(supplier)}`, data);

// Orders
export const fetchOrders = (
  slug: string,
  params: Record<string, string | number | undefined> = {},
): Promise<PaginatedOrders> => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  return api.get<PaginatedOrders>(`/t/${slug}/orders${qs.toString() ? "?" + qs : ""}`);
};

/** «Удалить» / «Восстановить» on the order list — this company's own list
 *  only; the partner keeps its copy, and nothing else moves (2026-09-24). */
export const deleteOrders = (slug: string, codes: string[]) =>
  api.post<{ count: number }>(`/t/${slug}/orders/delete`, { codes });
export const restoreOrders = (slug: string, codes: string[]) =>
  api.post<{ count: number }>(`/t/${slug}/orders/restore`, { codes });

export const fetchOrder = (slug: string, code: string): Promise<OrderDetail> =>
  api.get<OrderDetail>(`/t/${slug}/orders/${code}`);

/* ── Correcting an unsent order ────────────────────────────────────────────
   Only while the order is still «Создан (черновик)» and only for the company
   that placed it — the backend enforces both. Each call answers with the whole
   recomputed order, so the page never has to re-add up the totals itself. */

export const addOrderItem = (
  slug: string,
  code: string,
  productId: number,
  quantity = 1,
): Promise<OrderDetail> =>
  api.post<OrderDetail>(`/t/${slug}/orders/${code}/items`, {
    product_id: productId,
    quantity,
  });

export const updateOrderItem = (
  slug: string,
  code: string,
  itemId: number,
  quantity: number,
): Promise<OrderDetail> =>
  api.patch<OrderDetail>(`/t/${slug}/orders/${code}/items/${itemId}`, { quantity });

export const removeOrderItem = (
  slug: string,
  code: string,
  itemId: number,
): Promise<OrderDetail> => api.delete<OrderDetail>(`/t/${slug}/orders/${code}/items/${itemId}`);

// Counterparty filter dropdown — distinct companies on the other side of orders.
export type Counterparty = { slug: string; name: string };
/** Companies on the other side of our orders. `direction` scopes them to the
 *  side being viewed — clients on Incoming, suppliers on Outgoing. */
export const fetchOrderCounterparties = (
  slug: string,
  direction?: string,
): Promise<Counterparty[]> =>
  api.get<Counterparty[]>(
    `/t/${slug}/orders/meta/counterparties${direction ? `?direction=${direction}` : ""}`,
  );

/** An order still being composed — one per supplier, listed on Orders as a
 *  Draft. It becomes a real Order the moment it is sent. */
export type CartDraft = {
  id: number;
  seller_slug: string;
  seller_name: string;
  item_count: number;
  subtotal_eur: number;
  updated_at: string;
};
export const fetchCartDrafts = (slug: string): Promise<CartDraft[]> =>
  api.get<CartDraft[]>(`/t/${slug}/carts`);

// Clients the acting tenant may place an order on behalf of.
export type OrderClient = {
  slug: string;
  name: string;
  company_type: string;
  /** The client's standing «Экспорт» answer — prefills the direct invoice tick. */
  is_export?: boolean;
};
export const fetchOrderClients = (slug: string): Promise<OrderClient[]> =>
  api.get<OrderClient[]>(`/t/${slug}/orders/meta/clients`);

/** «Создать инвойс» → «Без заказа»: an order made to carry the invoice —
 *  already confirmed, no lines (they are written in the invoice). */
export const createDirectInvoiceOrder = (
  slug: string,
  payload: { buyer_slug: string; is_export: boolean; vat_zero?: boolean },
): Promise<OrderDetail> => api.post<OrderDetail>(`/t/${slug}/orders/direct-invoice`, payload);

/* Seller-side draft — an order being written down for a client, saved as it is
   typed. It becomes a real Order when Send is pressed. Distinct from CartDraft,
   which is the same idea on the buying side. */
export type SellerDraftItem = {
  product_id: number;
  product_name: string;
  product_sku: string;
  unit_price_eur: number;
  quantity: number;
  line_total_eur: number;
  /** The client's own price, and this order's own one when typed. */
  client_price_eur?: number;
  own_price_eur?: number | null;
  /** The lot picked off the shelf. */
  batch_no?: string;
  expires_on?: string | null;
  product_ean?: string;
  /** What is on the shelf of this article now, and its card's status. */
  stock?: number;
  is_orderable?: boolean;
  availability_status?: string;
  availability_expected_on?: string | null;
  /** The picked lot is on a shelf-life discount for this client (2026-09-28). */
  expiry_discount_pct?: number | string | null;
  expiry_saving_pct?: number | null;
  expiry_offer_kind?: string;
  expiry_offers?: import("./suppliers").ExpiryOffer[];
};

export type SellerDraft = {
  id: number;
  buyer_slug: string;
  buyer_name: string;
  notes: string;
  /** «Изменить цены текущего заказа». */
  edit_prices?: boolean;
  /** «ПВН = 0» for the order this becomes. */
  vat_zero?: boolean;
  item_count: number;
  subtotal_eur: number;
  items: SellerDraftItem[];
  updated_at: string;
};

export type SellerDraftPatch = {
  buyer_slug?: string;
  notes?: string;
  edit_prices?: boolean;
  vat_zero?: boolean;
  items?: {
    product_id: number;
    quantity: number;
    unit_price_eur?: number | null;
    batch_no?: string;
    expires_on?: string | null;
  }[];
};

export const fetchSellerDrafts = (slug: string): Promise<SellerDraft[]> =>
  api.get<SellerDraft[]>(`/t/${slug}/order-drafts`);

export const fetchSellerDraft = (slug: string, id: number): Promise<SellerDraft> =>
  api.get<SellerDraft>(`/t/${slug}/order-drafts/${id}`);

export const createSellerDraft = (slug: string, data: SellerDraftPatch = {}): Promise<SellerDraft> =>
  api.post<SellerDraft>(`/t/${slug}/order-drafts`, data);

export const updateSellerDraft = (
  slug: string,
  id: number,
  data: SellerDraftPatch,
): Promise<SellerDraft> => api.patch<SellerDraft>(`/t/${slug}/order-drafts/${id}`, data);

export const deleteSellerDraft = (slug: string, id: number): Promise<void> =>
  api.delete(`/t/${slug}/order-drafts/${id}`);

export const sendSellerDraft = (slug: string, id: number): Promise<OrderDetail> =>
  api.post<OrderDetail>(`/t/${slug}/order-drafts/${id}/send`, {});

export type ManualOrderItem = { product_id: number; quantity: number };
export const createManualOrder = (
  slug: string,
  data: {
    buyer_slug: string;
    buyer_name?: string;
    buyer_email?: string;
    notes?: string;
    items: ManualOrderItem[];
  },
): Promise<OrderDetail> => api.post<OrderDetail>(`/t/${slug}/orders/manual`, data);

export const transitionOrder = (
  slug: string,
  code: string,
  stage: Stage,
  note = "",
): Promise<OrderDetail> =>
  api.post<OrderDetail>(`/t/${slug}/orders/${code}/transition`, { stage, note });

/** Tick or untick «ПВН = 0» on the order — the seller's. Its sheets not yet
 *  handed over follow; the order's totals are recomputed. */
export async function setOrderVatZero(
  slug: string,
  code: string,
  vatZero: boolean,
): Promise<{ vat_zero: boolean }> {
  return api.put<{ vat_zero: boolean }>(`/t/${slug}/orders/${code}/vat-zero`, { vat_zero: vatZero });
}

/** Tick or untick «Экспорт» on the order. Either side of the trade may. */
export async function setOrderExport(
  slug: string,
  code: string,
  isExport: boolean,
): Promise<{ is_export: boolean }> {
  return api.put<{ is_export: boolean }>(`/t/${slug}/orders/${code}/export`, { is_export: isExport });
}

/** «Заявка на транспорт» on the order — opens the booking (Logistic tab). */
export async function setOrderTransport(
  slug: string,
  code: string,
  needsTransport: boolean,
): Promise<{ needs_transport: boolean }> {
  return api.put<{ needs_transport: boolean }>(`/t/${slug}/orders/${code}/transport`, {
    needs_transport: needsTransport,
  });
}
